import axios from 'axios';
import { authenticatedAxios } from './axiosConfig';
import type {
  HallucinationTest,
  HallucinationRun,
  HallucinationTestResult,
  CreateHallucinationTestRequest,
  UpdateHallucinationTestRequest,
  ToggleActiveRequest,
  StartRunRequest,
} from '../types/hallucination';

/**
 * API client service for hallucination testing system endpoints.
 * Provides methods for hallucination tests and runs.
 */
export class HallucinationApiService {
  private static instance: HallucinationApiService;

  private constructor() {}

  public static getInstance(): HallucinationApiService {
    if (!HallucinationApiService.instance) {
      HallucinationApiService.instance = new HallucinationApiService();
    }
    return HallucinationApiService.instance;
  }

  // ==================== Test CRUD Endpoints ====================

  /**
   * Create a new hallucination test.
   * @param request - The hallucination test configuration
   * @returns The created test ID
   */
  async createTest(request: CreateHallucinationTestRequest): Promise<string> {
    try {
      const response = await authenticatedAxios.post<{ testId: string }>(
        '/hallucination/tests',
        request
      );
      return response.data.testId;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to create hallucination test');
      }
      throw error;
    }
  }

  /**
   * List all hallucination tests.
   * @param includeInactive - Whether to include inactive tests (default: false)
   * @returns Array of hallucination tests
   */
  async listTests(includeInactive: boolean = false): Promise<HallucinationTest[]> {
    try {
      const response = await authenticatedAxios.get<HallucinationTest[]>(
        '/hallucination/tests',
        { params: { includeInactive } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to list hallucination tests');
      }
      throw error;
    }
  }

  /**
   * Get a hallucination test by ID.
   * @param testId - The test ID
   * @returns The hallucination test or null if not found
   */
  async getTest(testId: string): Promise<HallucinationTest | null> {
    try {
      const response = await authenticatedAxios.get<HallucinationTest>(
        `/hallucination/tests/${testId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get hallucination test');
      }
      throw error;
    }
  }

  /**
   * Update an existing hallucination test.
   * @param testId - The test ID
   * @param request - The updated test configuration
   * @returns True if successful
   */
  async updateTest(testId: string, request: UpdateHallucinationTestRequest): Promise<boolean> {
    try {
      await authenticatedAxios.put(
        `/hallucination/tests/${testId}`,
        request
      );
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to update hallucination test');
      }
      throw error;
    }
  }

  /**
   * Toggle the active status of a hallucination test.
   * @param testId - The test ID
   * @param isActive - The new active status
   * @returns True if successful
   */
  async toggleTest(testId: string, isActive: boolean): Promise<boolean> {
    try {
      const request: ToggleActiveRequest = { isActive };
      await authenticatedAxios.put(
        `/hallucination/tests/${testId}/toggle`,
        request
      );
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to toggle hallucination test');
      }
      throw error;
    }
  }

  /**
   * Delete a hallucination test.
   * @param testId - The test ID
   * @returns True if successful
   */
  async deleteTest(testId: string): Promise<boolean> {
    try {
      await authenticatedAxios.delete(
        `/hallucination/tests/${testId}`
      );
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to delete hallucination test');
      }
      throw error;
    }
  }

  // ==================== Run Endpoints ====================

  /**
   * Start a new hallucination run.
   * @param config - The provider configuration to use
   * @returns The created run ID
   */
  async startRun(config: StartRunRequest): Promise<string> {
    try {
      const response = await authenticatedAxios.post<{ runId: string }>(
        '/hallucination/runs',
        config
      );
      return response.data.runId;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to start hallucination run');
      }
      throw error;
    }
  }

  /**
   * List all hallucination runs.
   * @returns Array of hallucination runs
   */
  async listRuns(): Promise<HallucinationRun[]> {
    try {
      const response = await authenticatedAxios.get<HallucinationRun[]>(
        '/hallucination/runs'
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to list hallucination runs');
      }
      throw error;
    }
  }

  /**
   * Get a hallucination run by ID.
   * @param runId - The run ID
   * @returns The hallucination run or null if not found
   */
  async getRun(runId: string): Promise<HallucinationRun | null> {
    try {
      const response = await authenticatedAxios.get<HallucinationRun>(
        `/hallucination/runs/${runId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get hallucination run');
      }
      throw error;
    }
  }

  /**
   * Get results for a hallucination run.
   * @param runId - The run ID
   * @returns Array of test results
   */
  async getRunResults(runId: string): Promise<HallucinationTestResult[]> {
    try {
      const response = await authenticatedAxios.get<HallucinationTestResult[]>(
        `/hallucination/runs/${runId}/results`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get hallucination run results');
      }
      throw error;
    }
  }

  /**
   * Cancel a running hallucination run.
   * @param runId - The run ID
   * @returns True if successful
   */
  async cancelRun(runId: string): Promise<boolean> {
    try {
      await authenticatedAxios.put(
        `/hallucination/runs/${runId}/cancel`
      );
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to cancel hallucination run');
      }
      throw error;
    }
  }
}

export default HallucinationApiService;
